import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function StarRow({ value, onChange, size = 22, readonly = false }) {
  return (
    <div className="inline-flex items-center gap-1" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(n)}
          className={`p-0.5 ${readonly ? "cursor-default" : "cursor-pointer hover:scale-110 transition-transform"}`}
          data-testid={`star-${n}`}
          aria-label={`${n} star`}
        >
          <Star size={size} fill={n <= value ? "#C85A32" : "none"} color={n <= value ? "#C85A32" : "#9AA1A8"} />
        </button>
      ))}
    </div>
  );
}

/**
 * Renders the rating block for a completed booking. Both the viewer's own rating
 * (editable) and the other side's rating (read-only) are shown.
 */
export default function BookingRatingBlock({ bookingId, canRate, onSubmitted }) {
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState(null);
  const [other, setOther] = useState(null);
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${bookingId}/ratings`);
      setMine(data.mine || null);
      setOther(data.other || null);
      setStars(data.mine?.stars || 0);
      setReview(data.mine?.review || "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  const submit = async () => {
    if (!stars) return toast.error("Pick 1–5 stars");
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/rate`, { stars, review: review.trim() || null });
      toast.success(mine ? "Rating updated" : "Rating submitted — thanks for keeping LocaNest honest!");
      await load();
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loading) return null;

  return (
    <div className="mt-6 bg-[#F0EBE1] rounded-lg border border-[#1A1F2C]/10 p-6 space-y-5" data-testid="rating-block">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[#C85A32]">Your rating</div>
        {canRate ? (
          <div className="mt-3 space-y-3">
            <StarRow value={stars} onChange={setStars} />
            <Textarea placeholder="Optional — share what went well (or didn't)" value={review} onChange={(e) => setReview(e.target.value)} rows={3} data-testid="rating-review" />
            <Button onClick={submit} disabled={busy || !stars} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="rating-submit">
              {busy ? "Saving…" : mine ? "Update rating" : "Submit rating"}
            </Button>
          </div>
        ) : (
          <div className="mt-2 text-sm text-[#5C6B7A]">Rating unlocks after the deal is marked completed.</div>
        )}
      </div>

      <div className="border-t border-[#1A1F2C]/10 pt-4">
        <div className="text-xs uppercase tracking-[0.22em] text-[#5C6B7A]">The other party&apos;s rating</div>
        {other ? (
          <div className="mt-3">
            <StarRow value={other.stars} readonly size={18} />
            {other.review && <p className="mt-2 text-sm text-[#1A1F2C]">&ldquo;{other.review}&rdquo;</p>}
            <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-[#5C6B7A]">— {other.from_name}</div>
          </div>
        ) : (
          <div className="mt-2 text-sm text-[#5C6B7A]">Not submitted yet.</div>
        )}
      </div>
    </div>
  );
}
